import React, { useEffect, useRef } from 'react';
import './ResultsDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ResultsDashboard = ({ reportData, onRunAnother }) => {
  const iframeRef = useRef(null);

  // Listen for messages from the report iframe:
  //   - reportHeight → resize iframe
  //   - analytics    → bubble report-side events (tab switches, Calendly CTA,
  //                    Share clicks) up to the parent's GTM dataLayer
  useEffect(() => {
    const handleMessage = (e) => {
      if (!e.data) return;
      if (e.data.type === 'reportHeight' && iframeRef.current) {
        iframeRef.current.style.height = e.data.height + 'px';
      } else if (e.data.type === 'analytics' && e.data.event) {
        try {
          const { type, ...rest } = e.data;
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(rest);
        } catch (_) {}
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  // report_url is a relative path like /api/report/filename.html
  // Prepend REACT_APP_BACKEND_URL so the iframe can reach the backend
  const reportUrl = reportData?.report_url
    ? `${BACKEND_URL}${reportData.report_url}?embed=1`
    : null;

  if (!reportUrl) {
    // Partial-results fallback — if the HTML render failed but the audit
    // pipeline still produced a score, show the headline numbers instead of
    // a blank "unavailable" wall. Avoids losing the user when only the
    // final cosmetic step crashed.
    const finalScore   = reportData?.final_score;
    const grade        = reportData?.grade;
    const gradeLabel   = reportData?.grade_label;
    const hotelName    = reportData?.hotel_name;
    const currency     = reportData?.currency || 'USD';
    const revAtRisk    = reportData?.revenue_at_risk
                      ?? reportData?.room_revenue_at_risk
                      ?? reportData?.ai_query_revenue_at_risk
                      ?? null;
    const haveSummary  = typeof finalScore === 'number';

    if (haveSummary) {
      const fmtRevenue = (n) => {
        if (typeof n !== 'number' || !isFinite(n) || n <= 0) return null;
        try {
          return new Intl.NumberFormat('en-US', {
            style: 'currency', currency, maximumFractionDigits: 0,
          }).format(n);
        } catch (_) { return `${currency} ${Math.round(n).toLocaleString()}`; }
      };
      const revFmt = fmtRevenue(revAtRisk);
      return (
        <div className="db-error-wrap" data-testid="results-partial-summary">
          <div className="db-error-card">
            <h2>Full report unavailable</h2>
            <p>
              The audit completed but the visual report couldn't be generated.
              Here's the headline result we were able to compute:
            </p>
            <div
              style={{
                margin:'24px 0', padding:'24px', background:'#F8F9FF',
                border:'1px solid rgba(45,77,224,0.15)', borderRadius:14,
                fontFamily:"'DM Sans',sans-serif", color:'#0A0A1A',
              }}
            >
              {hotelName && (
                <div style={{fontSize:13, color:'rgba(10,10,26,0.65)', marginBottom:6}}>
                  {hotelName}
                </div>
              )}
              <div style={{display:'flex', alignItems:'baseline', gap:12, marginBottom: revFmt ? 12 : 0}}>
                <span style={{fontFamily:"'Outfit',sans-serif", fontSize:48, fontWeight:800, lineHeight:1, color:'#2D4DE0'}}>
                  {Math.round(finalScore)}
                </span>
                <span style={{fontSize:14, color:'rgba(10,10,26,0.55)'}}>/100</span>
                {grade && (
                  <span style={{
                    marginLeft:'auto', padding:'4px 10px', borderRadius:8,
                    background:'#EBF0FF', color:'#2D4DE0', fontWeight:700, fontSize:13,
                  }}>
                    {grade}{gradeLabel ? ` — ${gradeLabel}` : ''}
                  </span>
                )}
              </div>
              {revFmt && (
                <div style={{fontSize:13.5, color:'rgba(10,10,26,0.72)'}}>
                  Estimated revenue at risk: <strong>{revFmt}</strong>
                </div>
              )}
            </div>
            <p style={{fontSize:13, color:'rgba(10,10,26,0.62)', marginTop:8}}>
              We've logged this on our side — email{' '}
              <a href="mailto:hello@dhihospitality.com" style={{color:'#2D4DE0'}}>
                hello@dhihospitality.com
              </a>{' '}
              if you'd like the full report regenerated.
            </p>
            <button className="db-run-another-btn" onClick={onRunAnother}>
              Try Another Hotel →
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="db-error-wrap">
        <div className="db-error-card">
          <h2>Report unavailable</h2>
          <p>The audit completed but the report could not be generated.</p>
          <button className="db-run-another-btn" onClick={onRunAnother}>
            Try Another Hotel →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="db-iframe-wrap">
      <iframe
        ref={iframeRef}
        src={reportUrl}
        className="db-iframe"
        title={`AI Visibility Audit — ${reportData?.hotel_name || 'Report'}`}
        allowFullScreen
        // Intentionally no onLoad sizing. Earlier we measured the iframe's
        // own scrollHeight on load, which created a feedback loop: body
        // had `min-height:100vh`, so setting the iframe tall made body
        // taller, which made scrollHeight taller, which set iframe
        // taller... resulting in a large empty gap above the footer.
        // Now the report's own embed-mode script measures content height
        // via ResizeObserver and postMessages the correct value up —
        // shrinks as well as grows. The `onLoad` path is no longer needed.
      />
    </div>
  );
};

export default ResultsDashboard;
